import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { Button } from './ui.js';

/**
 * In-app confirmation, replacing the browser's `window.confirm`. Foundry never
 * uses native dialogs (see doc/DECISIONS.md) — they can't be styled and read as
 * jarring. `useConfirm()` returns a promise-based `confirm()` that mirrors the
 * imperative feel of `window.confirm` while rendering a branded modal.
 */
export interface ConfirmOptions {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as destructive (red). */
  danger?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn>(() => Promise.resolve(false));

/** `const confirm = useConfirm(); if (await confirm({ message })) …`. */
export function useConfirm(): ConfirmFn {
  return useContext(ConfirmContext);
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (options) => new Promise<boolean>((resolve) => setState({ options, resolve })),
    [],
  );

  const settle = useCallback((result: boolean) => {
    setState((current) => {
      current?.resolve(result);
      return null;
    });
  }, []);

  useEffect(() => {
    if (!state) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') settle(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, settle]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-secondary/40 p-4"
          role="presentation"
          onClick={() => settle(false)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl ring-1 ring-secondary/10"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            {state.options.title ? (
              <h2 className="mb-2 font-title text-xl tracking-wide">{state.options.title}</h2>
            ) : null}
            <div className="mb-6 text-sm leading-relaxed">{state.options.message}</div>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => settle(false)}>
                {state.options.cancelLabel ?? 'Cancel'}
              </Button>
              <Button
                autoFocus
                variant={state.options.danger ? 'danger' : 'primary'}
                onClick={() => settle(true)}
              >
                {state.options.confirmLabel ?? 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </ConfirmContext.Provider>
  );
}
